export const AI_OPERATIONS_MANAGER_SOLUTION_KEY = 'ai-operations-manager';
export const AI_OPERATIONS_MANAGER_SOLUTION_NAME = 'AI Operations Manager';
export const AI_OPERATIONS_MANAGER_CLASSIFICATION_LABEL = 'Specialised';
export const AI_OPERATIONS_MANAGER_SENDER_NAME = AI_OPERATIONS_MANAGER_SOLUTION_NAME;
export const AI_OPERATIONS_MANAGER_SENDER_EMAIL = 'ai-operations-manager@totalaccess.co.za';
export const AI_OPERATIONS_MANAGER_MENTION_ALIASES = ['@aiops', '@ai-ops', '@aioperations'];
export const AI_OPERATIONS_MANAGER_COMMAND_ALIASES = ['/ai', '/aiops'];
export const AI_OPERATIONS_MANAGER_THREAD_HINT = 'No @aiops needed — every message here is treated as an AI Ops request.';
export const AI_OPERATIONS_REVIEW_QUEUE_API_PATH = '/api/v1/work-items/ai-review-queue';
export const AI_OPERATIONS_REVIEW_VISIBLE_ITEM_TYPES = [
  'job',
  'appointment',
  'task',
  'subtask',
  'email_task',
  'project',
] as const;
export const AI_OPERATIONS_REVIEW_DATA_ITEM_TYPES = [
  'job',
  'appointment',
  'project',
] as const;

export type AiOperationsReviewStatus = 'queued' | 'running' | 'completed' | 'acknowledged' | 'on_hold' | 'resolved' | 'failed';
export type AiOperationsReviewVerdict = 'pass' | 'watch' | 'block' | 'unknown';
export type AiOperationsReviewManagerAction = 'acknowledge' | 'ai_hold' | 'resume_review' | 'resolve';
export type AiOperationsReviewVerdictFilter = 'all' | 'pass' | 'watch' | 'block';
export type AiOperationsReviewScope = 'appointment' | 'job' | 'project' | 'review_item' | 'work_item' | 'reschedule_request';

export interface AiOperationsReviewQueueItem {
  id: string;
  item_number: string;
  title: string;
  description: string;
  status: string;
  created_at: string;
  updated_at: string;
  verdict: AiOperationsReviewVerdict;
  confidence_score: number;
  finding_count: number;
  top_findings: string[];
  recommended_actions: string[];
  executive_summary: string;
  review_scope?: AiOperationsReviewScope | string | null;
  review_mode: string;
  review_status?: AiOperationsReviewStatus | string | null;
  manager_action?: AiOperationsReviewManagerAction | string | null;
  reviewed_at: string | null;
  anomalies: Array<{ code: string; message: string; severity: string; source: string }>;
  evidence_snapshot: {
    photo_count?: number;
    signature_count?: number;
    public_comment_count?: number;
    work_minutes?: number;
    travel_minutes?: number;
    total_appointment_expense_amount?: number;
    appointment_expense_count?: number;
  };
  receipt_evidence?: Array<{
    item_number?: string | null;
    title?: string | null;
    vendor_name?: string | null;
    amount?: number | null;
    receipt_photo_count?: number;
    receipt_photos: Array<{
      url: string;
      caption?: string | null;
      photo_type?: string | null;
    }>;
  }>;
  risk_scores: Record<string, number>;
  replacement_appointment_id?: string | null;
  replacement_appointment_number?: string | null;
  appointment: {
    id: string;
    title: string;
    number: string;
    status: string;
    subtype: string;
  };
  job: { id: string; title: string; number: string } | null;
  vehicle: { id: string; registration: string } | null;
}

export interface AiOperationsReviewQueueStats {
  pass: number;
  watch: number;
  block: number;
  total: number;
}

export interface AiOperationsReviewQueueResponse {
  success: boolean;
  items: AiOperationsReviewQueueItem[];
  stats: AiOperationsReviewQueueStats;
  pagination?: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface AiOperationsReviewQueueFetchOptions {
  token: string;
  verdict?: Exclude<AiOperationsReviewVerdictFilter, 'all'> | null;
  status?: string | null;
  search?: string | null;
  limit?: number;
  offset?: number;
  basePath?: string;
  signal?: AbortSignal;
}

export interface AiOperationsReviewChartDatum {
  key: string;
  name: string;
  value: number;
  color: string;
}

export interface AiOperationsReviewQueueAnalytics {
  verdictBreakdown: AiOperationsReviewChartDatum[];
  statusBreakdown: AiOperationsReviewChartDatum[];
  riskBreakdown: AiOperationsReviewChartDatum[];
  summary: {
    totalReviews: number;
    flaggedCount: number;
    blockedCount: number;
    watchCount: number;
    passCount: number;
    actionableCount: number;
    needsApprovalCount: number;
    queuedCount: number;
    runningCount: number;
    onHoldCount: number;
    resolvedCount: number;
    failedCount: number;
    replacementReadyCount: number;
    averageConfidence: number;
    averageFindings: number;
  };
}

export type AiOperationsManagerAlertStrictness = 'strict' | 'balanced' | 'relaxed';
export type AiOperationsManagerFindingSeverity = 'low' | 'medium' | 'high';
export type AiOperationsManagerAlertGroup = 'completion' | 'commercial' | 'operations';
export type AiOperationsManagerFindingCode =
  | 'required_expense_missing'
  | 'required_signature_missing'
  | 'required_photos_missing'
  | 'required_public_comment_missing'
  | 'required_odometer_missing'
  | 'pending_checklist_items'
  | 'form_failures_present'
  | 'expense_receipt_missing'
  | 'receipt_validation_mismatch'
  | 'receipt_amount_not_detected'
  | 'purchase_checklist_pending_items'
  | 'purchase_list_missing_expense_capture'
  | 'purchase_receipt_line_items_missing'
  | 'purchase_list_receipt_mismatch'
  | 'unexpected_receipt_items_detected'
  | 'vehicle_inspection_risk_present'
  | 'thin_completion_evidence'
  | 'field_photos_ai_flagged'
  | 'missing_after_photos'
  | 'missing_before_photos';

export interface AiOperationsManagerAlertOption {
  code: AiOperationsManagerFindingCode;
  group: AiOperationsManagerAlertGroup;
  severity: AiOperationsManagerFindingSeverity;
  label: string;
  description: string;
}

export interface AiOperationsManagerClientSettings {
  strictness: AiOperationsManagerAlertStrictness;
  findingOverrides: Partial<Record<AiOperationsManagerFindingCode, boolean>>;
  odometerEvidenceSources: {
    inspections: boolean;
    expenses: boolean;
    travelStart: boolean;
    latestAppointmentReading: boolean;
    completedTravel: boolean;
  };
}

export const AI_OPERATIONS_MANAGER_ALERT_GROUP_LABELS: Record<AiOperationsManagerAlertGroup, string> = {
  completion: 'Completion requirements',
  commercial: 'Commercial and purchasing',
  operations: 'Operational and advisory',
};

export const AI_OPERATIONS_MANAGER_ALERT_STRICTNESS_OPTIONS: Array<{
  value: AiOperationsManagerAlertStrictness;
  label: string;
  description: string;
}> = [
    {
      value: 'strict',
      label: 'Strict',
      description: 'Raise every supported alert unless you explicitly switch a rule off.',
    },
    {
      value: 'balanced',
      label: 'Balanced',
      description: 'Hide low-severity advisory noise by default, while keeping medium and high-risk findings on.',
    },
    {
      value: 'relaxed',
      label: 'Relaxed',
      description: 'Focus on high-risk blockers by default, with individual rules available as overrides.',
    },
  ];

export const AI_OPERATIONS_MANAGER_ALERT_OPTIONS: AiOperationsManagerAlertOption[] = [
  {
    code: 'required_odometer_missing',
    group: 'completion',
    severity: 'medium',
    label: 'Missing odometer evidence',
    description: 'Alert when a workflow expects mileage capture but no valid odometer evidence is found.',
  },
  {
    code: 'required_photos_missing',
    group: 'completion',
    severity: 'high',
    label: 'Missing photos',
    description: 'Alert when a workflow requires supporting photos and none were attached.',
  },
  {
    code: 'required_signature_missing',
    group: 'completion',
    severity: 'high',
    label: 'Missing signature',
    description: 'Alert when the appointment was resolved without the required sign-off.',
  },
  {
    code: 'required_public_comment_missing',
    group: 'completion',
    severity: 'medium',
    label: 'Missing closing comment',
    description: 'Alert when customer-facing closing notes were expected but not captured.',
  },
  {
    code: 'required_expense_missing',
    group: 'completion',
    severity: 'high',
    label: 'Missing required expense',
    description: 'Alert when an appointment type expects an expense capture and none is linked.',
  },
  {
    code: 'expense_receipt_missing',
    group: 'commercial',
    severity: 'medium',
    label: 'Expense receipts missing',
    description: 'Alert when appointment-linked expenses do not include receipt photos.',
  },
  {
    code: 'receipt_validation_mismatch',
    group: 'commercial',
    severity: 'high',
    label: 'Receipt amount mismatch',
    description: 'Alert when receipt AI finds a mismatch between the captured receipt and declared spend.',
  },
  {
    code: 'receipt_amount_not_detected',
    group: 'commercial',
    severity: 'medium',
    label: 'Receipt amount not detected',
    description: 'Alert when receipt AI cannot detect a usable amount for comparison.',
  },
  {
    code: 'purchase_checklist_pending_items',
    group: 'commercial',
    severity: 'medium',
    label: 'Purchase checklist still pending',
    description: 'Alert when purchase checklist items remain unresolved on a completed appointment.',
  },
  {
    code: 'purchase_list_missing_expense_capture',
    group: 'commercial',
    severity: 'high',
    label: 'Purchase checklist has no expense capture',
    description: 'Alert when planned purchases exist but no matching appointment-linked expense was captured.',
  },
  {
    code: 'purchase_receipt_line_items_missing',
    group: 'commercial',
    severity: 'medium',
    label: 'Receipt line items missing',
    description: 'Alert when purchase receipts do not provide enough line detail for checklist comparison.',
  },
  {
    code: 'purchase_list_receipt_mismatch',
    group: 'commercial',
    severity: 'high',
    label: 'Purchase items missing from receipt',
    description: 'Alert when planned purchase items cannot be matched to the captured slip evidence.',
  },
  {
    code: 'unexpected_receipt_items_detected',
    group: 'commercial',
    severity: 'medium',
    label: 'Unexpected receipt items detected',
    description: 'Alert when the captured receipt contains items not listed on the purchase checklist.',
  },
  {
    code: 'vehicle_inspection_risk_present',
    group: 'operations',
    severity: 'high',
    label: 'Vehicle inspection risk',
    description: 'Alert when linked vehicle inspections show failures, damage, or high AI fleet risk.',
  },
  {
    code: 'pending_checklist_items',
    group: 'operations',
    severity: 'medium',
    label: 'Checklist items still incomplete',
    description: 'Alert when checklist work is still outstanding on a resolved appointment.',
  },
  {
    code: 'form_failures_present',
    group: 'operations',
    severity: 'medium',
    label: 'Form failures present',
    description: 'Alert when linked forms contain failed or conditional sections that need review.',
  },
  {
    code: 'field_photos_ai_flagged',
    group: 'operations',
    severity: 'high',
    label: 'Field photos flagged by AI',
    description: 'Alert when AI believes supporting photos may be irrelevant or suspicious.',
  },
  {
    code: 'thin_completion_evidence',
    group: 'operations',
    severity: 'medium',
    label: 'Thin completion evidence',
    description: 'Alert when a resolved appointment has unusually little supporting evidence overall.',
  },
  {
    code: 'missing_after_photos',
    group: 'operations',
    severity: 'medium',
    label: 'Missing after photos',
    description: 'Alert when only before photos were captured and no completion photos are available.',
  },
  {
    code: 'missing_before_photos',
    group: 'operations',
    severity: 'low',
    label: 'Missing before photos',
    description: 'Alert when after photos were captured without a before baseline.',
  },
];

export const DEFAULT_AI_OPERATIONS_REVIEW_QUEUE_STATS: AiOperationsReviewQueueStats = {
  pass: 0,
  watch: 0,
  block: 0,
  total: 0,
};

const AI_OPERATIONS_REVIEW_VERDICT_META: Record<AiOperationsReviewVerdict, { label: string; color: string }> = {
  pass: { label: 'Pass', color: '#10b981' },
  watch: { label: 'Watch', color: '#f59e0b' },
  block: { label: 'Block', color: '#ef4444' },
  unknown: { label: 'Unknown', color: '#94a3b8' },
};

const AI_OPERATIONS_REVIEW_STATUS_META: Record<AiOperationsReviewStatus | 'unknown', { label: string; color: string }> = {
  queued: { label: 'Queued', color: '#2563eb' },
  running: { label: 'Running', color: '#6366f1' },
  completed: { label: 'Completed', color: '#10b981' },
  acknowledged: { label: 'Acknowledged', color: '#0ea5e9' },
  on_hold: { label: 'On Hold', color: '#f59e0b' },
  resolved: { label: 'Resolved', color: '#10b981' },
  failed: { label: 'Failed', color: '#ef4444' },
  unknown: { label: 'Unknown', color: '#94a3b8' },
};

const AI_OPERATIONS_REVIEW_RISK_META: Record<string, { label: string; color: string }> = {
  evidence: { label: 'Evidence', color: '#ef4444' },
  commercial: { label: 'Commercial', color: '#f97316' },
  compliance: { label: 'Compliance', color: '#eab308' },
  service: { label: 'Service', color: '#06b6d4' },
  operational: { label: 'Operational', color: '#8b5cf6' },
};

const AI_OPERATIONS_MANAGER_STRICTNESS_MINIMUM_SEVERITY: Record<AiOperationsManagerAlertStrictness, AiOperationsManagerFindingSeverity> = {
  strict: 'low',
  balanced: 'medium',
  relaxed: 'high',
};

const AI_OPERATIONS_MANAGER_FINDING_CODE_SET = new Set<AiOperationsManagerFindingCode>(
  AI_OPERATIONS_MANAGER_ALERT_OPTIONS.map((option) => option.code),
);

export const DEFAULT_AI_OPERATIONS_MANAGER_CLIENT_SETTINGS: AiOperationsManagerClientSettings = {
  strictness: 'strict',
  findingOverrides: {},
  odometerEvidenceSources: {
    inspections: true,
    expenses: true,
    travelStart: true,
    latestAppointmentReading: true,
    completedTravel: true,
  },
};

export type AiOperationsManagerPromptTrigger = 'mention' | 'command' | 'none';

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

export function getAiOperationsManagerMinimumSeverityForStrictness(
  strictness?: AiOperationsManagerAlertStrictness | null,
): AiOperationsManagerFindingSeverity {
  return AI_OPERATIONS_MANAGER_STRICTNESS_MINIMUM_SEVERITY[strictness || 'strict']
    || AI_OPERATIONS_MANAGER_STRICTNESS_MINIMUM_SEVERITY.strict;
}

export function isAiOperationsManagerFindingCode(value: string): value is AiOperationsManagerFindingCode {
  return AI_OPERATIONS_MANAGER_FINDING_CODE_SET.has(value as AiOperationsManagerFindingCode);
}

export function getAiOperationsManagerClientSettings(
  value?: unknown,
): AiOperationsManagerClientSettings {
  const record = asRecord(value);
  const strictness = record?.strictness;
  const normalizedStrictness: AiOperationsManagerAlertStrictness = strictness === 'balanced' || strictness === 'relaxed'
    ? strictness
    : 'strict';

  const rawFindingOverrides = asRecord(record?.findingOverrides);
  const findingOverrides = AI_OPERATIONS_MANAGER_ALERT_OPTIONS.reduce<Partial<Record<AiOperationsManagerFindingCode, boolean>>>((accumulator, option) => {
    const nextValue = rawFindingOverrides?.[option.code];
    if (typeof nextValue === 'boolean') {
      accumulator[option.code] = nextValue;
    }
    return accumulator;
  }, {});

  const rawOdometerSources = asRecord(record?.odometerEvidenceSources);

  return {
    strictness: normalizedStrictness,
    findingOverrides,
    odometerEvidenceSources: {
      inspections: asBoolean(rawOdometerSources?.inspections, DEFAULT_AI_OPERATIONS_MANAGER_CLIENT_SETTINGS.odometerEvidenceSources.inspections),
      expenses: asBoolean(rawOdometerSources?.expenses, DEFAULT_AI_OPERATIONS_MANAGER_CLIENT_SETTINGS.odometerEvidenceSources.expenses),
      travelStart: asBoolean(rawOdometerSources?.travelStart, DEFAULT_AI_OPERATIONS_MANAGER_CLIENT_SETTINGS.odometerEvidenceSources.travelStart),
      latestAppointmentReading: asBoolean(rawOdometerSources?.latestAppointmentReading, DEFAULT_AI_OPERATIONS_MANAGER_CLIENT_SETTINGS.odometerEvidenceSources.latestAppointmentReading),
      completedTravel: asBoolean(rawOdometerSources?.completedTravel, DEFAULT_AI_OPERATIONS_MANAGER_CLIENT_SETTINGS.odometerEvidenceSources.completedTravel),
    },
  };
}

export function isAiOperationsManagerFindingEnabled(args: {
  code: AiOperationsManagerFindingCode;
  severity: AiOperationsManagerFindingSeverity;
  settings?: AiOperationsManagerClientSettings | null;
}): boolean {
  const settings = args.settings || DEFAULT_AI_OPERATIONS_MANAGER_CLIENT_SETTINGS;
  const override = settings.findingOverrides[args.code];
  if (typeof override === 'boolean') {
    return override;
  }

  const severityRank: Record<AiOperationsManagerFindingSeverity, number> = {
    low: 1,
    medium: 2,
    high: 3,
  };

  return severityRank[args.severity] >= severityRank[getAiOperationsManagerMinimumSeverityForStrictness(settings.strictness)];
}

interface AiOperationsManagerThreadLike {
  solution_key?: string | null;
  thread_type?: string | null;
  thread_name?: string | null;
  title?: string | null;
}

export function isAiOperationsManagerSolutionKey(solutionKey?: string | null): boolean {
  return typeof solutionKey === 'string'
    && solutionKey.trim().toLowerCase() === AI_OPERATIONS_MANAGER_SOLUTION_KEY;
}

export function canShowAiOperationsReviewTabForItemType(itemType?: string | null): boolean {
  const normalizedItemType = typeof itemType === 'string'
    ? itemType.trim().toLowerCase()
    : '';

  return AI_OPERATIONS_REVIEW_VISIBLE_ITEM_TYPES.includes(
    normalizedItemType as (typeof AI_OPERATIONS_REVIEW_VISIBLE_ITEM_TYPES)[number],
  );
}

export function supportsAiOperationsReviewDataForWorkItem(
  itemType?: string | null,
  itemSubtype?: string | null,
): boolean {
  const normalizedItemType = typeof itemType === 'string'
    ? itemType.trim().toLowerCase()
    : '';
  const normalizedItemSubtype = typeof itemSubtype === 'string'
    ? itemSubtype.trim().toLowerCase()
    : '';

  return AI_OPERATIONS_REVIEW_DATA_ITEM_TYPES.includes(
    normalizedItemType as (typeof AI_OPERATIONS_REVIEW_DATA_ITEM_TYPES)[number],
  )
    || (normalizedItemType === 'inspection' && normalizedItemSubtype === 'operations_review');
}

export function formatAiOperationsReviewLabel(value?: string | null): string {
  return (value || 'Unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function normalizeAiOperationsReviewLifecycleStatus(
  value?: string | null,
): AiOperationsReviewStatus | 'unknown' {
  const normalizedValue = typeof value === 'string'
    ? value.trim().toLowerCase()
    : '';

  switch (normalizedValue) {
    case 'queued':
      return 'queued';
    case 'running':
    case 'in_progress':
      return 'running';
    case 'completed':
    case 'complete':
      return 'completed';
    case 'acknowledged':
      return 'acknowledged';
    case 'on_hold':
      return 'on_hold';
    case 'resolved':
      return 'resolved';
    case 'failed':
      return 'failed';
    default:
      return 'unknown';
  }
}

export function getAiOperationsReviewStatusMeta(value?: string | null) {
  return AI_OPERATIONS_REVIEW_STATUS_META[normalizeAiOperationsReviewLifecycleStatus(value)];
}

export function isAiOperationsReviewActionable(item: Pick<AiOperationsReviewQueueItem, 'status' | 'review_status'>): boolean {
  const reviewStatus = normalizeAiOperationsReviewLifecycleStatus(item.review_status || item.status);
  return !['completed', 'resolved', 'failed'].includes(reviewStatus);
}

function toFiniteNumber(value: unknown): number {
  return Number.isFinite(Number(value)) ? Number(value) : 0;
}

export function buildAiOperationsReviewQueueAnalytics(
  items: AiOperationsReviewQueueItem[],
  stats: AiOperationsReviewQueueStats = DEFAULT_AI_OPERATIONS_REVIEW_QUEUE_STATS,
): AiOperationsReviewQueueAnalytics {
  const verdictCounts: Record<AiOperationsReviewVerdict, number> = {
    pass: Math.max(0, toFiniteNumber(stats.pass)),
    watch: Math.max(0, toFiniteNumber(stats.watch)),
    block: Math.max(0, toFiniteNumber(stats.block)),
    unknown: 0,
  };

  const statusCounts = new Map<string, number>();
  const riskTotals = new Map<string, { total: number; count: number }>();
  let actionableCount = 0;
  let needsApprovalCount = 0;
  let replacementReadyCount = 0;
  let confidenceTotal = 0;
  let findingTotal = 0;

  items.forEach((item) => {
    const verdictKey = (typeof item.verdict === 'string' ? item.verdict.toLowerCase() : 'unknown') as AiOperationsReviewVerdict;
    if (!['pass', 'watch', 'block'].includes(verdictKey)) {
      verdictCounts.unknown += 1;
    }

    const lifecycleStatus = normalizeAiOperationsReviewLifecycleStatus(item.review_status || item.status);
    statusCounts.set(lifecycleStatus, (statusCounts.get(lifecycleStatus) || 0) + 1);

    if (isAiOperationsReviewActionable(item)) {
      actionableCount += 1;
    }

    if (
      isAiOperationsReviewActionable(item)
      && (item.verdict === 'watch' || item.verdict === 'block' || item.review_scope === 'reschedule_request')
    ) {
      needsApprovalCount += 1;
    }

    if (item.replacement_appointment_id) {
      replacementReadyCount += 1;
    }

    confidenceTotal += Math.max(0, toFiniteNumber(item.confidence_score));
    findingTotal += Math.max(0, toFiniteNumber(item.finding_count));

    Object.entries(item.risk_scores || {}).forEach(([key, value]) => {
      const numericValue = Math.max(0, toFiniteNumber(value));
      if (numericValue <= 0) {
        return;
      }

      const existing = riskTotals.get(key) || { total: 0, count: 0 };
      existing.total += numericValue;
      existing.count += 1;
      riskTotals.set(key, existing);
    });
  });

  const totalReviews = Math.max(
    Math.max(0, toFiniteNumber(stats.total)),
    verdictCounts.pass + verdictCounts.watch + verdictCounts.block + verdictCounts.unknown,
    items.length,
  );

  const verdictBreakdown = (Object.entries(AI_OPERATIONS_REVIEW_VERDICT_META) as Array<[AiOperationsReviewVerdict, { label: string; color: string }]>)
    .map(([key, meta]) => ({
      key,
      name: meta.label,
      value: verdictCounts[key],
      color: meta.color,
    }))
    .filter((entry) => entry.value > 0 || entry.key !== 'unknown');

  const statusBreakdown = Array.from(statusCounts.entries())
    .map(([key, value]) => {
      const meta = AI_OPERATIONS_REVIEW_STATUS_META[key as AiOperationsReviewStatus | 'unknown'] || AI_OPERATIONS_REVIEW_STATUS_META.unknown;
      return {
        key,
        name: meta.label,
        value,
        color: meta.color,
      };
    })
    .sort((left, right) => right.value - left.value);

  const riskBreakdown = Array.from(riskTotals.entries())
    .map(([key, totals]) => {
      const meta = AI_OPERATIONS_REVIEW_RISK_META[key] || { label: formatAiOperationsReviewLabel(key), color: '#94a3b8' };
      return {
        key,
        name: meta.label,
        value: Number((totals.total / Math.max(1, totals.count)).toFixed(1)),
        color: meta.color,
      };
    })
    .sort((left, right) => right.value - left.value);

  return {
    verdictBreakdown,
    statusBreakdown,
    riskBreakdown,
    summary: {
      totalReviews,
      flaggedCount: verdictCounts.block + verdictCounts.watch,
      blockedCount: verdictCounts.block,
      watchCount: verdictCounts.watch,
      passCount: verdictCounts.pass,
      actionableCount,
      needsApprovalCount,
      queuedCount: statusCounts.get('queued') || 0,
      runningCount: statusCounts.get('running') || 0,
      onHoldCount: statusCounts.get('on_hold') || 0,
      resolvedCount: (statusCounts.get('resolved') || 0) + (statusCounts.get('completed') || 0),
      failedCount: statusCounts.get('failed') || 0,
      replacementReadyCount,
      averageConfidence: items.length > 0 ? Number((confidenceTotal / items.length).toFixed(1)) : 0,
      averageFindings: items.length > 0 ? Number((findingTotal / items.length).toFixed(1)) : 0,
    },
  };
}

export async function fetchAiOperationsReviewQueue(
  options: AiOperationsReviewQueueFetchOptions,
): Promise<AiOperationsReviewQueueResponse | null> {
  if (!options.token) {
    return null;
  }

  const params = new URLSearchParams();
  if (options.verdict) params.set('verdict', options.verdict);
  if (options.status) params.set('status', options.status);
  if (options.search) params.set('search', options.search);
  params.set('limit', String(Math.min(Math.max(options.limit || 100, 1), 200)));
  params.set('offset', String(Math.max(options.offset || 0, 0)));

  const basePath = (options.basePath || '').replace(/\/$/, '');
  const response = await fetch(`${basePath}${AI_OPERATIONS_REVIEW_QUEUE_API_PATH}?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${options.token}`,
    },
    signal: options.signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch AI review queue (${response.status})`);
  }

  const data = await response.json() as AiOperationsReviewQueueResponse;
  return data?.success ? data : null;
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildAliasPattern(aliases: string[]): string {
  return aliases.map((alias) => escapeRegex(alias)).join('|');
}

function normalizePromptText(value: string): string {
  return value
    .replace(/\s+/g, ' ')
    .trim();
}

function stripCommandInvocation(message: string): string | null {
  const commandPattern = new RegExp(`^(?:${buildAliasPattern(AI_OPERATIONS_MANAGER_COMMAND_ALIASES)})(?=$|[\\s,:;.!?\\-])`, 'i');
  const match = commandPattern.exec(message);
  if (!match) return null;

  return normalizePromptText(message.slice(match[0].length).replace(/^[\s,:;.!?\-]+/, ''));
}

function stripMentionInvocation(message: string): string | null {
  const mentionPattern = new RegExp(`(^|\\s)(?:${buildAliasPattern(AI_OPERATIONS_MANAGER_MENTION_ALIASES)})(?=$|[\\s,:;.!?\\-])`, 'i');
  const match = mentionPattern.exec(message);
  if (!match) return null;

  const before = message.slice(0, match.index).trim();
  const after = message
    .slice(match.index + match[0].length)
    .replace(/^[\s,:;.!?\-]+/, '')
    .trim();

  return normalizePromptText([before, after].filter(Boolean).join(' '));
}

export function getAiOperationsManagerPromptFromMessage(message?: string | null): {
  isInvocation: boolean;
  shouldRespond: boolean;
  prompt: string | null;
  trigger: AiOperationsManagerPromptTrigger;
} {
  const trimmedMessage = typeof message === 'string' ? message.trim() : '';
  if (!trimmedMessage) {
    return {
      isInvocation: false,
      shouldRespond: false,
      prompt: null,
      trigger: 'none',
    };
  }

  const lowercaseMessage = trimmedMessage.toLowerCase();

  for (const alias of AI_OPERATIONS_MANAGER_COMMAND_ALIASES) {
    if (!lowercaseMessage.startsWith(alias)) continue;

    const prompt = stripCommandInvocation(trimmedMessage);
    if (prompt === null) continue;

    return {
      isInvocation: true,
      shouldRespond: prompt.length > 0,
      prompt: prompt || null,
      trigger: 'command',
    };
  }

  for (const alias of AI_OPERATIONS_MANAGER_MENTION_ALIASES) {
    if (!lowercaseMessage.includes(alias)) continue;

    const prompt = stripMentionInvocation(trimmedMessage);
    if (prompt === null) continue;

    return {
      isInvocation: true,
      shouldRespond: prompt.length > 0,
      prompt: prompt || null,
      trigger: 'mention',
    };
  }

  return {
    isInvocation: false,
    shouldRespond: false,
    prompt: null,
    trigger: 'none',
  };
}

export function isAiOperationsManagerThread(thread?: AiOperationsManagerThreadLike | null): boolean {
  if (!thread) return false;

  const solutionKey = typeof thread.solution_key === 'string'
    ? thread.solution_key.trim().toLowerCase()
    : '';

  if (isAiOperationsManagerSolutionKey(solutionKey)) {
    return true;
  }

  const threadName = [thread.thread_name, thread.title]
    .find((value): value is string => typeof value === 'string' && value.trim().length > 0)
    ?.trim()
    .toLowerCase();

  return thread.thread_type === 'team' && threadName === AI_OPERATIONS_MANAGER_SOLUTION_NAME.toLowerCase();
}

const AI_OPERATIONS_MANAGER_SENDER_AVATAR_SVG = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96" fill="none">
    <defs>
      <linearGradient id="aiOpsGradient" x1="10" y1="10" x2="86" y2="86" gradientUnits="userSpaceOnUse">
        <stop stop-color="#0f766e"/>
        <stop offset="1" stop-color="#0ea5e9"/>
      </linearGradient>
    </defs>
    <rect width="96" height="96" rx="48" fill="url(#aiOpsGradient)"/>
    <path d="M48 19L68 28V45C68 58 58 69 48 76C38 69 28 58 28 45V28L48 19Z" fill="white" fill-opacity="0.18"/>
    <text x="48" y="57" text-anchor="middle" font-size="28" font-family="Inter, Arial, sans-serif" font-weight="700" fill="white">AI</text>
  </svg>`.trim(),
);
export const AI_OPERATIONS_MANAGER_SENDER_AVATAR = `data:image/svg+xml;charset=UTF-8,${AI_OPERATIONS_MANAGER_SENDER_AVATAR_SVG}`;

export function getSolutionTeamDefaultFeaturePolicies(solutionKey?: string | null) {
  if (isAiOperationsManagerSolutionKey(solutionKey)) {
    return {
      message_centre: true,
      job_threads: false,
      video_calls: false,
      voice_calls: false,
      my_tasks: true,
      total_assistant: true,
      show_when_online: true,
    };
  }

  return {
    message_centre: true,
    job_threads: true,
    video_calls: true,
    voice_calls: true,
    my_tasks: true,
    total_assistant: true,
    show_when_online: true,
  };
}